import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";

import {
  createGrantOpportunityMilestone,
  deleteGrantOpportunityMilestone,
  listGrantOpportunityMilestones,
  updateGrantOpportunityMilestone,
  type GrantOpportunityMilestone,
  type GrantOpportunityMilestoneStage,
} from "@/api/grant_opportunity_milestones";
import {
  createGrantOpportunityTask,
  deleteGrantOpportunityTask,
  listGrantOpportunityTasks,
  updateGrantOpportunityTask,
  type GrantOpportunityTaskKind,
} from "@/api/grant_opportunity_tasks";
import { listMunicipalityCollaborators } from "@/api/municipality_collaborators";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  GRANT_TASK_KIND_LABELS,
  GRANT_TASK_KIND_OPTIONS,
} from "@/components/grant_suite/grantSuiteDisplay";
import { GrantNotesDisclosure } from "@/components/grant_suite/GrantNotesDisclosure";
import {
  MilestoneOwnerSelect,
  ownerSelectOptions,
} from "@/components/grant_suite/MilestoneOwnerSelect";
import { InlineEditableTitle } from "@/components/workflow_tracking/InlineEditableTitle";
import { WorkflowTaskChecklist } from "@/components/workflow_tracking/WorkflowTaskChecklist";
import { useCurrentUser } from "@/context/UserProvider";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import {
  grantOpportunityEventsQueryKey,
  invalidateGrantSuitePipeline,
} from "@/lib/grantSuiteQueryKeys";

const MILESTONE_STAGE_ORDER: GrantOpportunityMilestoneStage[] = [
  "preparing",
  "submitted",
  "awarded",
];

const MILESTONE_STAGE_LABELS: Record<GrantOpportunityMilestoneStage, string> = {
  preparing: "Preparing",
  submitted: "Submitted",
  awarded: "Awarded",
};

type Collaborator = { id: number; email: string };

type GrantOpportunityTasksProps = {
  municipalityId: number;
  opportunityId: number;
  // Task assignees on a department-scoped opportunity must be department
  // members (ENG-1824), so the picker narrows to them.
  departmentId?: number | null;
  pursuing: boolean;
  awarded: boolean;
  ownerName?: string | null;
};

export function GrantOpportunityTasks({
  municipalityId,
  opportunityId,
  departmentId = null,
  pursuing,
  awarded,
  ownerName,
}: GrantOpportunityTasksProps) {
  const currentUser = useCurrentUser();
  const milestonesQueryKey = [
    "grant_opportunity_milestones",
    municipalityId,
    opportunityId,
  ] as const;
  const ownerLabel = ownerName?.trim() || "Unassigned";

  const {
    data: milestones = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: milestonesQueryKey,
    queryFn: () => listGrantOpportunityMilestones(municipalityId, opportunityId),
  });

  const outsideUser = userIsOutsideUser(currentUser);
  const { data: collaborators = [], isLoading: collaboratorsLoading } = useQuery({
    queryKey: ["municipality_collaborators", municipalityId, departmentId ?? null],
    queryFn: () => listMunicipalityCollaborators(municipalityId, departmentId),
    enabled: !outsideUser,
  });

  const groups = MILESTONE_STAGE_ORDER.map((stage) => ({
    stage,
    milestones: milestones.filter((milestone) => milestone.stage === stage),
  })).filter((group) => group.milestones.length > 0);

  return (
    <div className="space-y-4" data-testid="grant-opportunity-milestones">
      <div className="space-y-1">
        <h3 className="text-base font-semibold">Milestones</h3>
        <p className="text-sm text-muted-foreground">
          {outsideUser
            ? "Milestones and tasks the team is tracking for this grant."
            : awarded
              ? "Group post-award work into milestones; nest tasks and obligations under each."
              : pursuing
                ? "Group the application into milestones by stage, then nest checkable tasks under each."
                : "Plan milestones now — they stay with this grant when you start pursuing."}
        </p>
        <p className="text-xs text-muted-foreground">
          Grant owner: <span className="font-medium text-foreground">{ownerLabel}</span>
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading milestones…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">Could not load milestones.</p>
      ) : milestones.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/10 px-4 py-5 text-center text-sm text-muted-foreground">
          {outsideUser ? "No milestones yet." : "No milestones yet. Add the first one below."}
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.stage} className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {MILESTONE_STAGE_LABELS[group.stage]}
              </h4>
              {group.milestones.map((milestone) => (
                <MilestoneCard
                  key={milestone.id}
                  milestone={milestone}
                  municipalityId={municipalityId}
                  opportunityId={opportunityId}
                  awarded={awarded}
                  collaborators={collaborators}
                  collaboratorsLoading={collaboratorsLoading}
                  currentUserId={currentUser?.id ?? null}
                  milestonesQueryKey={milestonesQueryKey}
                  readOnly={outsideUser}
                />
              ))}
            </section>
          ))}
        </div>
      )}

      {!outsideUser && (
        <AddMilestoneForm
          municipalityId={municipalityId}
          opportunityId={opportunityId}
          defaultStage={awarded ? "awarded" : pursuing ? "preparing" : "preparing"}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          milestonesQueryKey={milestonesQueryKey}
        />
      )}
    </div>
  );
}

function MilestoneCard({
  milestone,
  municipalityId,
  opportunityId,
  awarded,
  collaborators,
  collaboratorsLoading,
  currentUserId,
  milestonesQueryKey,
  readOnly = false,
}: {
  milestone: GrantOpportunityMilestone;
  municipalityId: number;
  opportunityId: number;
  awarded: boolean;
  collaborators: Collaborator[];
  collaboratorsLoading: boolean;
  currentUserId: number | null;
  milestonesQueryKey: readonly unknown[];
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const tasksQueryKey = [
    "grant_opportunity_milestone_tasks",
    municipalityId,
    opportunityId,
    milestone.id,
  ] as const;
  const eventsQueryKey = grantOpportunityEventsQueryKey(municipalityId, opportunityId);

  const refreshMilestones = () =>
    queryClient.invalidateQueries({ queryKey: milestonesQueryKey });

  const ownerMutation = useMutation({
    mutationFn: (ownerId: number | null) =>
      updateGrantOpportunityMilestone(municipalityId, opportunityId, milestone.id, {
        owner_id: ownerId,
      }),
    onSuccess: refreshMilestones,
  });

  const titleMutation = useMutation({
    mutationFn: (title: string) =>
      updateGrantOpportunityMilestone(municipalityId, opportunityId, milestone.id, { title }),
    onSuccess: refreshMilestones,
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      deleteGrantOpportunityMilestone(municipalityId, opportunityId, milestone.id),
    onSuccess: () => {
      refreshMilestones();
      queryClient.invalidateQueries({ queryKey: eventsQueryKey });
      invalidateGrantSuitePipeline(queryClient, municipalityId);
    },
  });

  const ownerOptions = ownerSelectOptions(milestone.owner_id, milestone.owner_name, collaborators);

  return (
    <div className="space-y-3 rounded-lg border bg-background p-4" data-testid="grant-milestone-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          {readOnly ? (
            <p className="min-w-0 break-words text-sm font-semibold">{milestone.title}</p>
          ) : (
            <InlineEditableTitle
              value={milestone.title}
              onSubmit={(next) => titleMutation.mutate(next)}
              inputAriaLabel="Edit milestone title"
              editButtonAriaLabel={`Rename milestone ${milestone.title}`}
              className="text-sm font-semibold"
            />
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="font-normal">
              {milestone.completed_task_count}/{milestone.task_count} tasks
            </Badge>
          </div>
        </div>
        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => deleteMutation.mutate()}
            aria-label={`Delete milestone ${milestone.title}`}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {readOnly ? (
        <p className="text-xs text-muted-foreground">
          Owner:{" "}
          <span className="font-medium text-foreground">
            {milestone.owner_name ?? "No owner"}
          </span>
        </p>
      ) : (
        <div className="max-w-xs">
          <Label htmlFor={`milestone-owner-${milestone.id}`} className="sr-only">
            Owner for {milestone.title}
          </Label>
          <MilestoneOwnerSelect
            id={`milestone-owner-${milestone.id}`}
            ariaLabel={`Owner for ${milestone.title}`}
            value={milestone.owner_id}
            onChange={(ownerId) => ownerMutation.mutate(ownerId)}
            collaborators={ownerOptions}
            loading={collaboratorsLoading}
            disabled={ownerMutation.isPending}
            triggerClassName="h-8 text-xs"
          />
        </div>
      )}

      <WorkflowTaskChecklist
        testId={`grant-milestone-${milestone.id}-tasks`}
        heading="Tasks"
        description={
          readOnly
            ? "Steps the team is tracking for this milestone."
            : "Break this milestone into checkable steps for your team."
        }
        tasksQueryKey={tasksQueryKey}
        eventsQueryKey={eventsQueryKey}
        listTasks={() =>
          listGrantOpportunityTasks(municipalityId, opportunityId).then((tasks) =>
            tasks.filter((task) => task.grant_opportunity_milestone_id === milestone.id),
          )
        }
        createTask={({ kind, ...payload }) =>
          createGrantOpportunityTask(municipalityId, opportunityId, {
            ...payload,
            kind: kind as GrantOpportunityTaskKind | undefined,
            grant_opportunity_milestone_id: milestone.id,
          })
        }
        updateTask={(taskId, patch) =>
          updateGrantOpportunityTask(municipalityId, opportunityId, taskId, patch)
        }
        deleteTask={(taskId) => deleteGrantOpportunityTask(municipalityId, opportunityId, taskId)}
        onInvalidatePipeline={(client) => {
          invalidateGrantSuitePipeline(client, municipalityId);
          client.invalidateQueries({ queryKey: milestonesQueryKey });
        }}
        emptyMessage={readOnly ? "No tasks yet." : "No tasks yet. Add the first step below."}
        loadingMessage="Loading tasks…"
        newItemPlaceholder="Task title…"
        addButtonLabel="Add task"
        createFormVariant="boxed"
        allowRenameTask
        showTaskStatus
        dueDateMode="summary"
        readOnly={readOnly}
        // Without assigneeSelect the checklist falls back to "Assigned to
        // {name}" text — exactly the read-only rendering we want.
        assigneeSelect={
          readOnly
            ? undefined
            : {
                collaborators,
                currentUserId,
                loading: collaboratorsLoading,
              }
        }
        kindSelect={
          awarded
            ? { options: GRANT_TASK_KIND_OPTIONS, defaultValue: "general", ariaLabel: "Type" }
            : undefined
        }
        renderTaskBadge={(task) =>
          task.kind && task.kind !== "general" ? (
            <Badge variant="outline" className="text-[10px]">
              {GRANT_TASK_KIND_LABELS[task.kind as GrantOpportunityTaskKind] ?? task.kind}
            </Badge>
          ) : null
        }
        // Notes are internal-team context and their read is denied for
        // outside users, so the disclosures disappear entirely (ENG-1877).
        renderTaskFooter={
          readOnly
            ? undefined
            : (task) => (
                <GrantNotesDisclosure
                  municipalityId={municipalityId}
                  opportunityId={opportunityId}
                  notable={{ type: "task", taskId: task.id }}
                  currentUserId={currentUserId}
                />
              )
        }
      />

      {!readOnly && (
        <GrantNotesDisclosure
          municipalityId={municipalityId}
          opportunityId={opportunityId}
          notable={{ type: "milestone", milestoneId: milestone.id }}
          currentUserId={currentUserId}
          label="Milestone notes"
        />
      )}
    </div>
  );
}

function AddMilestoneForm({
  municipalityId,
  opportunityId,
  defaultStage,
  collaborators,
  collaboratorsLoading,
  milestonesQueryKey,
}: {
  municipalityId: number;
  opportunityId: number;
  defaultStage: GrantOpportunityMilestoneStage;
  collaborators: Collaborator[];
  collaboratorsLoading: boolean;
  milestonesQueryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [stage, setStage] = useState<GrantOpportunityMilestoneStage>(defaultStage);
  const [ownerId, setOwnerId] = useState<number | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createGrantOpportunityMilestone(municipalityId, opportunityId, {
        title: title.trim(),
        stage,
        owner_id: ownerId,
      }),
    onSuccess: () => {
      setTitle("");
      setOwnerId(null);
      queryClient.invalidateQueries({ queryKey: milestonesQueryKey });
    },
  });

  return (
    <div className="space-y-2 rounded-lg border bg-muted/10 p-3">
      <p className="text-xs font-medium text-muted-foreground">Add a milestone</p>
      <div className="flex flex-wrap gap-2">
        <Label htmlFor="new-milestone-title" className="sr-only">
          Milestone title
        </Label>
        <Input
          id="new-milestone-title"
          className="min-w-[10rem] grow-[2] basis-56"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Milestone title…"
          aria-label="Milestone title"
        />
        <Select
          value={stage}
          onValueChange={(value) => setStage(value as GrantOpportunityMilestoneStage)}
        >
          <SelectTrigger className="min-w-[10rem] grow basis-40" aria-label="Stage">
            <SelectValue>{MILESTONE_STAGE_LABELS[stage]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {MILESTONE_STAGE_ORDER.map((value) => (
              <SelectItem key={value} value={value}>
                {MILESTONE_STAGE_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <MilestoneOwnerSelect
          ariaLabel="Milestone owner"
          value={ownerId}
          onChange={setOwnerId}
          collaborators={collaborators}
          loading={collaboratorsLoading}
          triggerClassName="min-w-[11rem] grow basis-44"
        />
        <Button
          type="button"
          size="sm"
          disabled={!title.trim() || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          <Plus className="mr-2 h-3.5 w-3.5" />
          {createMutation.isPending ? "Adding…" : "Add milestone"}
        </Button>
      </div>
    </div>
  );
}
